import React, { createContext, useContext, useState, useEffect } from 'react';
import axiosInstance from '../utils/axiosConfig';
import { useAuth } from './AuthContext';
import { verifyToken } from '../utils/axiosConfig';
import moment from 'moment-timezone';

const UserContext = createContext();

export const useUser = () => {
  return useContext(UserContext);
};

export const UserProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [isOnboarded, setIsOnboarded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Get browser timezone
  const browserTimezone = moment.tz.guess() || 'UTC';
  
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

  // Fetch user profile from the API
  const fetchUserProfile = async () => {
    if (!currentUser) return;
    
    // Ensure token is valid before making the call
    await verifyToken();
    
    try {
      setLoading(true);
      const response = await axiosInstance.get('/users/profile');
      console.log("Profile response:", response.data);
      
      // If user doesn't have a timezone set, update it with browser timezone
      if (response.data && !response.data.timezone) {
        const tempProfile = { ...response.data, timezone: browserTimezone };
        setProfile(tempProfile);
        
        // Also update on the server
        try {
          await axiosInstance.put('/users/timezone', { timezone: browserTimezone });
          console.log(`Updated user timezone to ${browserTimezone}`);
        } catch (err) {
          console.error('Failed to update timezone:', err);
        }
      } else {
        setProfile(response.data);
      }
      
      setIsOnboarded(response.data.onboarded);
      setLoading(false);
    } catch (err) {
      // Check if this is a 404 error (new user, profile not found)
      if (err.response && err.response.status === 404) {
        console.log("New user detected - profile not yet created");
        // Set a minimal profile for new users
        setProfile({
          timezone: browserTimezone,
          onboarded: false
        });
        
        // You might want to redirect to an onboarding flow here
        setIsOnboarded(false);
      } else {
        setError(err.message || 'Error fetching user profile');
      }
      setLoading(false);
    }
  };

  // Create or update user profile
  const updateProfile = async (data) => {
    try {
      setLoading(true);
      
      // Ensure timezone is included in the data
      const profileData = {
        ...data,
        timezone: data.timezone || browserTimezone
      };
      
      const response = await axiosInstance.post('/users/profile', profileData);
      
      setProfile(response.data);
      setIsOnboarded(true);
      
      // Store onboarding status in localStorage for quicker reference
      localStorage.setItem('isOnboarded', 'true');
      
      setLoading(false);
      return response.data;
    } catch (err) {
      setError(err.message || 'Error updating profile');
      setLoading(false);
      throw err;
    }
  };

  // Update user's timezone
  const updateTimezone = async (timezone) => {
    try {
      console.log('Frontend: Attempting to update timezone:', timezone);
      console.log('Current base URL:', axiosInstance.defaults.baseURL);
      
      const response = await axiosInstance.put('/users/timezone', { timezone });
      
      console.log('Frontend: Timezone update response:', response.data);
      
      setProfile({
        ...profile,
        timezone: response.data.timezone
      });
      
      setLoading(false);
      return response.data;
    } catch (err) {
      console.error('Frontend: Detailed timezone update error:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        config: err.config
      });
      
      setError(err.response?.data?.message || err.message || 'Error updating timezone');
      setLoading(false);
      throw err;
    }
  };

  // Update monthly spending limit
  const updateMonthlyLimit = async (monthlyLimit) => {
    try {
      setLoading(true);
      const response = await axiosInstance.put('/users/monthly-limit', { monthlyLimit });
      setProfile({
        ...profile,
        currentMonthlyLimit: response.data.currentMonthlyLimit
      });
      setLoading(false);
      return response.data;
    } catch (err) {
      setError(err.message || 'Error updating monthly limit');
      setLoading(false);
      throw err;
    }
  };

  // Delete user account (from the backend)
  const deleteAccount = async () => {
    try {
      setLoading(true);
      await axiosInstance.delete('/users/account');
      clearUserData();
      setLoading(false);
      return true;
    } catch (err) {
      setError(err.message || 'Error deleting account');
      setLoading(false);
      throw err;
    }
  };

  // Clear user data (used on logout)
  const clearUserData = () => {
    setProfile(null);
    setIsOnboarded(false);
    localStorage.removeItem('isOnboarded');
  };

  // Load user profile when currentUser changes
  useEffect(() => {
    if (currentUser) {
      // Check localStorage first for quick initial state
      const storedOnboardingStatus = localStorage.getItem('isOnboarded');
      if (storedOnboardingStatus === 'true') {
        setIsOnboarded(true);
      }
      
      // Still fetch from API for authoritative data
      fetchUserProfile();
    } else {
      clearUserData();
    }
  }, [currentUser]);

  const value = {
    profile,
    isOnboarded,
    loading,
    error,
    setError,
    fetchUserProfile,
    updateProfile,
    updateTimezone,
    updateMonthlyLimit,
    deleteAccount,
    clearUserData,
    browserTimezone
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

export default UserContext;